import { useCloudStatus } from "../../hooks/use-cloud-status";

import { ReactNode, useCallback } from "react";
import { ScreenManagerProvider } from "../../hooks/use-screen-manager";
import {
    Config,
    PROVISIONING,
    ProvisioningScreenKey,
    ScreenConfig
} from "../../utils/screen-manager/utils";
import { AsyncArchitectureErrorView } from "../error/async-architecture-error-view";
import ErrorView from "../error/error-view";
import RobotsView from "../robots/robots-view";
import { ScreenManagerView } from "../screen-manager";
import TimeoutView from "../timeout/timeout-view";

export type BaseProvisioningUIProps = {
    screenDictionary?: Partial<Record<ProvisioningScreenKey, ReactNode>>;
    screenConfig?: Partial<Record<ProvisioningScreenKey, Config>>;
    screenFallback?: JSX.Element;
};

const baseLoadingScreen = <RobotsView />;

const baseScreenDictionary = {
    [PROVISIONING]: baseLoadingScreen
};

const baseScreenConfig: ScreenConfig = {
    [PROVISIONING]: {
        position: { default: 100 },
        onMount: () => console.log("loading default provisioning UI"),
        visibility: {
            default: true
        }
    }
};

/**
 * Base component to render a provisioning UI for multiple products.
 * @param {BaseProvisioningUIProps} - prop that contains screen configuration.
 * @returns @type {JSX.Element}
 */
export const BaseProvisioningUI = ({
    screenDictionary = baseScreenDictionary,
    screenConfig = baseScreenConfig,
    screenFallback = baseLoadingScreen
}: BaseProvisioningUIProps) => {
    const { createCloudStatus } = useCloudStatus();

    // override the current screen based on cloud status error or timeout.
    const getScreenOverride = useCallback(() => {
        switch (createCloudStatus) {
            case "error":
                return <ErrorView />;
            case "error-unrecoverable":
                return (
                    <AsyncArchitectureErrorView errorType="un-recoverable" />
                );
            case "timeout":
                return <TimeoutView />;
            case "timeout-recoverable":
                return <AsyncArchitectureErrorView errorType="recoverable" />;
        }

        return undefined;
    }, [createCloudStatus]);

    return (
        <ScreenManagerProvider
            screenDictionary={screenDictionary}
            screensConfig={screenConfig}
        >
            <ScreenManagerView
                screenOverride={getScreenOverride()}
                screenFallback={screenFallback}
            />
        </ScreenManagerProvider>
    );
};
